import { MaintenanceModel, MaintenanceWorkOrderModel } from 'api/models';
import { LabelColorType } from 'components/Label/labelTypes';

// `NonNullable` because all four model fields are optional, so the union carried
// `undefined`, which cannot be a `Record` key.
type MaintenanceStatusValue = NonNullable<
  | MaintenanceModel['status']
  | MaintenanceModel['result']
  | MaintenanceModel['urgency']
  | MaintenanceWorkOrderModel['status']
>;

/**
 * `satisfies` rather than a `Record` annotation. This map has never been total:
 * seven members of the union above (`Other`, `HomeownerFixing`, `Invoicing`,
 * `FixedByRequester` and three more) have no entry, so a lookup for one already
 * yielded `undefined`. `Partial<Record<…>>` would describe that, but it would
 * also make the twenty-two keys that *are* present read as possibly-undefined.
 * `satisfies` keeps each of those exact while still checking that no key strays
 * outside the union. See docs/development/typecheck-baseline-followups.md.
 */
export const MaintenanceStatus = {
  // MaintenanceModel['status']
  New: 'New',
  InProgress: 'In Progress',
  Closed: 'Closed',
  // MaintenanceModel['result']
  Cancelled: 'Cancelled',
  Completed: 'Completed',
  Duplicate: 'Duplicate',
  Postponed: 'Postponed',
  WorkingAsDesigned: 'Working As Designed',
  NotRecommended: 'Not Recommended',
  NotApproved: 'Not Approved',
  NotReproducible: 'Not Reproducible',
  RevertNotice: 'Revert Notice',
  // MaintenanceModel['urgency']
  NotSpecified: 'NotSpecified',
  Emergency: 'Emergency',
  Urgent: 'Urgent',
  Routine: 'Routine',
  AssigningPro: 'Assigning Pro',
  AppointmentScheduled: 'Scheduled',
  WorkInProgress: 'Work In Progress',
  PreparingQuote: 'Preparing Quote',
  WaitingForQuoteApproval: 'Approval Required',
  PreparingInvoice: 'PreparingInvoice',
  Canceled: 'Cancelled',
} as const satisfies Partial<Record<MaintenanceStatusValue, string>>;

type WorkOrderStatusItem = {
  color: string;
  label: string;
};

export const workOrderStatus: Record<
  NonNullable<MaintenanceWorkOrderModel['status']>,
  WorkOrderStatusItem
> = {
  AppointmentScheduled: {
    color: 'text-blue',
    label: 'Appointment Scheduled',
  },
  AssigningPro: {
    color: 'text-blue',
    label: 'Assigning Pro',
  },
  Canceled: {
    color: 'text-red',
    label: 'Cancelled',
  },
  Completed: {
    color: 'text-green',
    label: 'Completed',
  },
  New: {
    color: 'text-navy',
    label: 'New',
  },
  PreparingInvoice: {
    color: 'text-blue',
    label: 'Preparing Invoice',
  },
  PreparingQuote: {
    color: 'text-blue',
    label: 'Preparing Quote',
  },
  WaitingForQuoteApproval: {
    color: 'text-red',
    label: 'Approval Required',
  },
  WorkInProgress: {
    color: 'text-blue',
    label: 'In Progress',
  },
};

export const workOrderStatusLabel: Record<
  NonNullable<MaintenanceWorkOrderModel['status']>,
  LabelColorType
> = {
  AppointmentScheduled: 'blue',
  AssigningPro: 'blue',
  Canceled: 'red-border',
  Completed: 'green',
  New: 'navy',
  PreparingInvoice: 'blue',
  PreparingQuote: 'blue',
  WaitingForQuoteApproval: 'red-border',
  WorkInProgress: 'blue',
};
